import {
  ComponentProps,
  FC,
  LegacyRef,
  SVGProps,
  Suspense,
  forwardRef,
  lazy,
} from 'react'

import Skeleton from 'components/Skeleton'
import joinClassNames from 'utilities/joinClassNames'

import { IconName } from './icon-names'
import styles from './styles.module.scss'
import { NestedColors } from '../types'

type SVGElement = SVGProps<SVGSVGElement>

export interface IconProps extends Omit<ComponentProps<'img'>, 'fill' | 'ref'> {
  name?: IconName
  url?: string
  color?: NestedColors
}

const iconCache = new Map<IconName, ReturnType<typeof lazy>>()

const getIconComponent = (name: IconName) => {
  if (!iconCache.has(name)) {
    const Component = lazy(async () => ({
      default: (await import(`../../assets/svg/${name}.svg`))
        .ReactComponent as FC<SVGElement>,
    }))

    iconCache.set(name, Component)
  }

  return iconCache.get(name)!
}

const Icon = forwardRef<SVGSVGElement, IconProps>(
  (
    {
      name = 'avatar',
      url,
      width = 16,
      height,
      color = 'accent-brand-default',
      style,
      className,
      ...props
    },
    ref,
  ) => {
    const size = { width, height: height || width }

    if (url) {
      return (
        <img
          {...props}
          ref={ref as LegacyRef<HTMLImageElement>}
          src={url}
          role="img"
          className={joinClassNames(styles.icon, className)}
          {...size}
        />
      )
    }

    const IconComponent = getIconComponent(name)

    return (
      <Suspense fallback={<Skeleton width={width} height={height || width} />}>
        <IconComponent
          ref={ref}
          {...props}
          role="img"
          width={width}
          height={height || width}
          style={{
            '--icon-current-color': `var(--${color})`,
            ...style,
          }}
          className={joinClassNames(styles.icon, className)}
        />
      </Suspense>
    )
  },
)

Icon.displayName = 'Icon'

export default Icon
